import * as React from 'react';
import { Logo } from '../components/Logo/Logo';
import { Stepper, type StepperStep } from '../components/Stepper';
import { useConfig } from '../hooks';

const DefaultRailFooter: React.FC = () => {
  const config = useConfig();
  const shopName = config?.shopConfig?.content?.shopName;
  // The shop's Shopify contact address, carried on the storefront config
  // because these screens render before anyone is signed in to ask the partner
  // API. Someone stuck part-way through signing up needs somewhere to write to,
  // and the sentence telling them the shop is who to ask was doing that job
  // with no way to act on it.
  const contactEmail = config?.shopConfig?.shop?.contactEmail;
  if (!shopName) return null;

  return (
    <>
      Invited by <b className="font-semibold text-foreground">{shopName}</b> as a seller.
      Questions?{' '}
      {contactEmail ? (
        <a
          href={`mailto:${contactEmail}`}
          className="font-medium text-primary hover:underline"
        >
          Contact the shop
        </a>
      ) : (
        <span className="font-medium text-foreground">Contact the shop</span>
      )}
      .
    </>
  );
};

/** The marketplace's mark and its name, side by side. */
const ShopBrand: React.FC = () => {
  const config = useConfig();
  const shopName = config?.shopConfig?.content?.shopName;

  return (
    <div className="flex items-center gap-2.5">
      <Logo maxWidth={28} maxHeight={28} />
      {shopName && (
        <span className="text-sm font-semibold tracking-tight text-foreground">{shopName}</span>
      )}
    </div>
  );
};

export type OnboardingStepKey = 'invitation' | 'details' | 'terms';

const stepsFor = (current: string): StepperStep[] => [
  // Past tense on the first step: by the time the rail is on screen the
  // password is already set, and the design words it as the fact it is.
  { key: 'invitation', label: 'Accept invitation', hint: 'Password set' },
  { key: 'details', label: 'Your details', hint: "Who's selling" },
  {
    key: 'terms',
    label: 'Terms',
    // From earlier in the journey this step is "One agreement" — what it is.
    // Standing on it, the useful fact is that it is the last one, which is
    // what the design shows and what stops it reading as open-ended.
    hint: current === 'terms' ? 'Last step' : 'One agreement',
  },
];

export interface OnboardingLayoutProps {
  step: OnboardingStepKey;
  title: string;
  subtitle?: React.ReactNode;
  /** Rendered above the content (e.g. an error alert). */
  banner?: React.ReactNode;
  /** Rendered under the stepper — e.g. who invited them. */
  railFooter?: React.ReactNode;
  /** Widens the content column; the terms document needs a readable measure. */
  width?: 'form' | 'document';
  children: React.ReactNode;
}

/**
 * The shell for the three screens that make up joining a marketplace.
 *
 * Accepting an invitation, giving details and agreeing to terms were three
 * unconnected 448px-wide cards, so an invitee two screens in had no way of
 * knowing whether they were nearly done. The rail shows the whole journey with
 * the completed steps ticked, which answers that before it is asked.
 *
 * The heading stays an h2 — the auth e2e specs address these screens by
 * `heading, level: 2`, and the level carries no design meaning here.
 */
export const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({
  step,
  title,
  subtitle,
  banner,
  railFooter,
  width = 'form',
  children,
}) => (
  // Surface behind, card rail in front: on an all-white page the rail and the
  // form were the same sheet of paper, so the journey didn't read as a rail.
  <div className="flex min-h-screen flex-col bg-surface lg:grid lg:grid-cols-[15rem_1fr]">
    <aside className="border-b border-border bg-card px-6 py-6 lg:border-b-0 lg:border-r lg:py-8">
      {/* Logo *and* the marketplace's name, as drawn — an invitee arriving
          cold needs to know whose marketplace they are joining, and a logo
          alone doesn't always say it. */}
      <ShopBrand />
      <Stepper className="mt-7" steps={stepsFor(step)} currentKey={step} />
      {/* Who invited them, on every step of the journey rather than only the
          first — two screens in, "whose marketplace is this again?" is a
          question the rail should already have answered. */}
      <div className="mt-6 text-xs leading-5 text-muted-foreground">
        {railFooter ?? <DefaultRailFooter />}
      </div>
    </aside>

    {/* Centred in the space beside the rail, per the spike: pinned hard left it
        sat against the rail's border with a wide empty margin on the right, so
        the page read as two columns that had drifted apart. */}
    <main className="flex justify-center px-6 py-11 sm:px-10">
      <div className={width === 'document' ? 'w-full max-w-2xl' : 'w-full max-w-[30rem]'}>
        <h2 className="text-[22px] font-semibold leading-tight tracking-tight text-foreground">{title}</h2>
        {subtitle && <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>}
        {banner && <div className="mt-5">{banner}</div>}
        <div className="mt-6">{children}</div>
      </div>
    </main>
  </div>
);
